import React from 'react';
import { cn } from '@/lib/utils';

export const BrandHeroCopy = ({
  logo,
  logoAlt,
  eyebrow,
  phrases,
  subtitle,
  features = [],
  featuresInline = false,
  tone = 'burgundy',
  actions,
}) => {
  const isForest = tone === 'forest';
  const titleClass = isForest ? 'text-white' : 'text-white';
  const ledeClass = isForest ? 'text-white/85' : 'text-white/85';
  const eyebrowClass = isForest ? 'text-white/75' : 'text-white/75';
  const featureClass = isForest ? 'text-white' : 'text-white';

  return (
    <div
      className={cn(
        'flex h-full w-full flex-col items-center justify-center text-center lg:items-start lg:text-left',
        featuresInline ? 'max-w-5xl' : features.length > 0 ? 'max-w-4xl' : 'max-w-xl',
      )}
    >
      {logo ? (
        <div className="flex items-center justify-center gap-3 lg:justify-start">
          {(Array.isArray(logo) ? logo : [logo]).map((src, index) => (
            <img
              key={src}
              src={src}
              alt={index === 0 ? logoAlt : ''}
              className="h-12 w-auto max-w-[200px] object-contain object-center drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)] sm:h-14 lg:h-[4.25rem] lg:max-w-[240px] lg:object-left"
            />
          ))}
        </div>
      ) : null}
      {eyebrow ? (
        <p className={cn('mt-5 text-xs font-semibold uppercase tracking-[0.18em] sm:mt-6 sm:text-sm', eyebrowClass)}>
          {eyebrow}
        </p>
      ) : null}
      <h1 className={cn('mt-3 text-balance text-[1.85rem] font-bold leading-[1.15] sm:mt-4 sm:text-4xl lg:text-5xl', titleClass)}>
        {phrases.map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </h1>
      {subtitle ? (
        <p className={cn('mx-auto mt-4 max-w-md text-sm font-medium leading-relaxed sm:mt-5 sm:text-base lg:mx-0', ledeClass)}>
          {subtitle}
        </p>
      ) : null}
      {actions ? (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3 sm:mt-7 lg:justify-start">
          {actions}
        </div>
      ) : null}
      {features.length > 0 ? (
        <ul
          className={cn(
            'mt-8 w-full gap-x-4 gap-y-4 sm:mt-10 sm:gap-x-6 lg:gap-x-8',
            !featuresInline && features.length === 3
              ? 'mx-auto grid max-w-md grid-cols-2 justify-items-center lg:mx-0 lg:flex lg:max-w-none lg:flex-nowrap lg:justify-start'
              : 'flex flex-wrap items-start justify-center lg:flex-nowrap lg:justify-start',
          )}
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const isLastOfThree = !featuresInline && features.length === 3 && index === 2;
            return (
              <li
                key={feature.label}
                className={cn(
                  'flex items-start gap-2 lg:shrink-0',
                  featureClass,
                  isLastOfThree && 'col-span-2 lg:col-auto',
                  'justify-center text-left lg:justify-start',
                )}
              >
                {Icon ? <Icon size={18} weight="duotone" className="mt-0.5 shrink-0" /> : null}
                <span className="text-left">
                  <span className="block text-xs font-semibold leading-tight sm:text-[13px]">{feature.label}</span>
                  {feature.sub ? (
                    <span className="mt-0.5 block text-[11px] font-medium leading-snug opacity-75">{feature.sub}</span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

const BrandHero = ({
  image,
  imageAlt,
  wash = 'from-black/45 via-black/20 to-transparent',
  className,
  children,
}) => (
  <section className={cn('relative overflow-hidden bg-neutral-950', className)}>
    <img
      src={image}
      alt={imageAlt}
      className="absolute inset-0 h-full w-full object-cover object-left lg:object-[center_center]"
    />
    <div className="absolute inset-0 bg-black/40" />
    <div className={cn('absolute inset-0 bg-gradient-to-r', wash)} />
    <div className="landing-hero-offset relative z-10 mx-auto flex min-h-[640px] w-full max-w-6xl items-stretch justify-center px-6 pb-12 sm:px-8 sm:pb-14 lg:min-h-[78vh] lg:justify-start lg:px-10 lg:pb-16">
      {children}
    </div>
  </section>
);

export default BrandHero;
